import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  PRAYERS,
  SALAH_METRICS,
  TIMELINESS,
  addMonths,
  daysInMonth,
  fromIso,
  toIso,
  weekdayOf,
  type Building,
  type Prayer,
  type SalahEntry,
  type SalahMarks,
  type SalahMetric,
  type Timeliness,
} from '@map/shared';
import { salahPoints, scoreSalah } from '@map/health';
import {
  deleteSalahEntry,
  fetchSalahHistory,
  fetchSalahSettings,
  saveSalahEntry,
  saveSalahSettings,
} from './api.js';
import { healthColor } from '../styles/tokens.js';

/**
 * A prayer's building, as a log rather than a construction site: its rating,
 * a month of days to pick from, and the entry for the day picked. Any day can
 * be filled in or corrected after the fact; the rating follows.
 */

/** Out of five, as the same colour the map uses for how things are going. */
export function ratingColour(rating: number): string {
  return healthColor(1 - rating / 5);
}

export function formatRating(rating: number): string {
  return rating.toFixed(1);
}

const WEEKDAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function useSalahOrder() {
  return useQuery({ queryKey: ['salah-settings'], queryFn: fetchSalahSettings });
}

function useRefreshSalah(buildingId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['salah', buildingId] });
    await queryClient.invalidateQueries({ queryKey: ['planet-buildings'] });
    await queryClient.invalidateQueries({ queryKey: ['universe'] });
  };
}

export function SalahLog({ building }: { building: Building }) {
  const history = useQuery({
    queryKey: ['salah', building.id],
    queryFn: () => fetchSalahHistory(building.id),
  });
  const order = useSalahOrder();

  const today = history.data?.days.at(-1)?.day ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? today;

  const entries = useMemo(
    () => new Map((history.data?.entries ?? []).map((e) => [e.day, e])),
    [history.data],
  );
  const days = useMemo(
    () => new Map((history.data?.days ?? []).map((d) => [d.day, d])),
    [history.data],
  );

  if (!history.data || !order.data || !today || !day) {
    return <p className="text-sm text-text-muted">…</p>;
  }

  const prayer = history.data.prayer;
  const rating = history.data.rating;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl tabular-nums" style={{ color: ratingColour(rating) }}>
          {formatRating(rating)}
        </span>
        <span className="text-sm text-text-muted">out of 5</span>
        <span className="ml-auto text-xs text-text-muted">
          {PRAYERS.find((p) => p.key === prayer)?.name}
        </span>
      </div>

      <MiniCalendar
        today={today}
        selected={day}
        onPick={setPicked}
        scoreOf={(d) => days.get(d)?.score ?? null}
        counted={(d) => days.has(d)}
      />

      <EntryEditor
        key={`${building.id}-${day}`}
        buildingId={building.id}
        prayer={prayer}
        day={day}
        isToday={day === today}
        entry={entries.get(day) ?? null}
        order={order.data.order}
      />

      <SalahRecords days={history.data.days} entries={entries} onPick={setPicked} />
    </div>
  );
}

function MiniCalendar({
  today,
  selected,
  onPick,
  scoreOf,
  counted,
}: {
  today: string;
  selected: string;
  onPick: (day: string) => void;
  scoreOf: (day: string) => number | null;
  counted: (day: string) => boolean;
}) {
  const [month, setMonth] = useState(() => {
    const d = fromIso(selected);
    return { year: d.year, month: d.month };
  });
  useEffect(() => {
    const d = fromIso(selected);
    setMonth({ year: d.year, month: d.month });
  }, [selected]);

  const first = { ...month, day: 1 };
  const lead = (weekdayOf(first) + 6) % 7; // Monday first
  const length = daysInMonth(month.year, month.month);
  const title = new Date(Date.UTC(month.year, month.month - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const atThisMonth = today.slice(0, 7) === toIso(first).slice(0, 7);

  const step = (n: number) => {
    const next = addMonths(first, n);
    setMonth({ year: next.year, month: next.month });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-sm">
        <button
          onClick={() => step(-1)}
          className="px-2 text-text-muted hover:text-text"
          aria-label="Previous month"
        >
          ‹
        </button>
        <span>{title}</span>
        <button
          onClick={() => step(1)}
          disabled={atThisMonth}
          className="px-2 text-text-muted hover:text-text disabled:opacity-30"
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[0.6875rem]">
        {WEEKDAY_LETTERS.map((letter, i) => (
          <span key={i} className="text-text-muted">
            {letter}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`lead-${i}`} />
        ))}
        {Array.from({ length }, (_, i) => {
          const day = toIso({ ...month, day: i + 1 });
          const future = day > today;
          const score = scoreOf(day);
          // Unlogged is neutral, so it is marked quietly, not in red.
          const missed = !future && score === null && counted(day) && day !== today;
          const isSelected = day === selected;
          return (
            <button
              key={day}
              disabled={future}
              onClick={() => onPick(day)}
              className="flex aspect-square items-center justify-center rounded-[var(--radius-sm)] tabular-nums disabled:opacity-25"
              style={{
                background:
                  score !== null
                    ? `color-mix(in oklch, ${ratingColour(score)} 55%, transparent)`
                    : 'var(--surface-1)',
                outline: isSelected
                  ? '2px solid var(--text)'
                  : missed
                    ? '1px dashed var(--border)'
                    : day === today
                      ? '1px solid var(--border)'
                      : 'none',
                outlineOffset: '-1px',
              }}
              title={
                score !== null
                  ? `${day}: ${formatRating(score)} / 5`
                  : missed
                    ? `${day}: not logged`
                    : day
              }
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Everything done is the starting point: you mark what was missing, not what was there. */
function fullMarks(): SalahMarks {
  return Object.fromEntries(
    SALAH_METRICS.map((m) => [m.key, m.input === 'scale' ? 5 : 1]),
  ) as SalahMarks;
}

function EntryEditor({
  buildingId,
  prayer,
  day,
  isToday,
  entry,
  order,
}: {
  buildingId: string;
  prayer: Prayer;
  day: string;
  isToday: boolean;
  entry: SalahEntry | null;
  order: SalahMetric[];
}) {
  const refresh = useRefreshSalah(buildingId);
  const [timeliness, setTimeliness] = useState<Timeliness>(entry?.timeliness ?? 'on_time');
  const [marks, setMarks] = useState<SalahMarks>({ ...fullMarks(), ...(entry?.marks ?? {}) });

  const save = useMutation({
    mutationFn: () => saveSalahEntry(buildingId, day, { timeliness, marks }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => deleteSalahEntry(buildingId, day),
    onSuccess: refresh,
  });

  const points = salahPoints(prayer, order);
  const score = scoreSalah(prayer, { timeliness, marks }, order);
  const dirty =
    !entry ||
    entry.timeliness !== timeliness ||
    SALAH_METRICS.some(
      (m) => (entry.marks[m.key] ?? (m.input === 'scale' ? 5 : 1)) !== marks[m.key],
    );

  const label = isToday
    ? 'Today'
    : new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });

  return (
    <section className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-border p-3">
      <header className="flex items-baseline gap-2">
        <h4 className="text-sm">{label}</h4>
        <span className="text-xs text-text-muted">{entry ? 'logged' : 'not logged'}</span>
        <span className="ml-auto text-sm tabular-nums" style={{ color: ratingColour(score) }}>
          {formatRating(score)} / 5
        </span>
      </header>

      <div className="grid grid-cols-3 gap-1">
        {TIMELINESS.map((option) => (
          <button
            key={option.key}
            onClick={() => setTimeliness(option.key)}
            className="rounded-[var(--radius-sm)] border px-2 py-1.5 text-xs"
            style={{
              borderColor: timeliness === option.key ? 'var(--accent)' : 'var(--border)',
              background: timeliness === option.key ? 'var(--surface-2)' : 'transparent',
              color: timeliness === option.key ? 'var(--text)' : 'var(--text-muted)',
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <ul className="flex flex-col">
        {SALAH_METRICS.filter((m) => m.applies(prayer)).map((metric) => {
          const value = marks[metric.key] ?? (metric.input === 'scale' ? 5 : 1);
          const worth = points.get(metric.key) ?? 0;
          if (metric.input === 'scale') {
            return (
              <li key={metric.key} className="flex items-center gap-3 py-1.5">
                <span className="w-36 shrink-0 text-sm">
                  {metric.label}
                  <span className="block text-[0.6875rem] text-text-muted">{metric.hint}</span>
                </span>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={value}
                  onChange={(e) => setMarks({ ...marks, [metric.key]: Number(e.target.value) })}
                  className="min-w-0 flex-1"
                  aria-label={metric.label}
                />
                <span className="w-8 text-right text-xs tabular-nums text-text-muted">
                  {value}/5
                </span>
              </li>
            );
          }
          const done = value >= 1;
          return (
            <li key={metric.key}>
              <button
                onClick={() => setMarks({ ...marks, [metric.key]: done ? 0 : 1 })}
                className="flex w-full items-center gap-3 rounded-[var(--radius-sm)] py-1.5 text-left hover:bg-surface-1"
                aria-pressed={done}
              >
                <span
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[0.625rem]"
                  style={{
                    borderColor: done ? 'var(--health-0)' : 'var(--border)',
                    background: done ? 'var(--health-0)' : 'transparent',
                    color: 'var(--surface-0)',
                  }}
                  aria-hidden
                >
                  {done ? '✓' : ''}
                </span>
                <span
                  className="flex-1 text-sm"
                  style={{ color: done ? 'var(--text)' : 'var(--text-muted)' }}
                >
                  {metric.label}
                  <span className="ml-2 text-[0.6875rem] text-text-muted">{metric.hint}</span>
                </span>
                <span className="text-[0.6875rem] tabular-nums text-text-muted">
                  {worth.toFixed(1)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !dirty}
          className="rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-50"
          style={{ borderColor: dirty ? 'var(--accent)' : undefined }}
        >
          {save.isPending ? 'Saving…' : entry ? 'Save changes' : 'Log it'}
        </button>
        {entry && (
          <button
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm text-text-muted hover:text-text"
          >
            Clear day
          </button>
        )}
      </div>
      {(save.isError || remove.isError) && (
        <p className="text-xs text-text-muted">
          That did not save: {(save.error ?? remove.error)?.message}
        </p>
      )}
    </section>
  );
}

/** Every logged day, newest first, with what it scored and where it left the rating. */
function SalahRecords({
  days,
  entries,
  onPick,
}: {
  days: Array<{ day: string; score: number | null; rating: number }>;
  entries: Map<string, SalahEntry>;
  onPick: (day: string) => void;
}) {
  const [all, setAll] = useState(false);
  const rows = [...days].reverse();
  const shown = all ? rows : rows.slice(0, 14);

  return (
    <section className="flex flex-col gap-1">
      <h4 className="text-xs uppercase tracking-wide text-text-muted">Record</h4>
      <table className="w-full text-xs tabular-nums">
        <thead className="text-text-muted">
          <tr>
            <th className="py-1 text-left font-normal">Day</th>
            <th className="py-1 text-left font-normal">Prayed</th>
            <th className="py-1 text-right font-normal">Score</th>
            <th className="py-1 text-right font-normal">Rating</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const entry = entries.get(row.day);
            return (
              <tr
                key={row.day}
                onClick={() => onPick(row.day)}
                className="cursor-pointer hover:bg-surface-1"
              >
                <td className="py-1">{row.day}</td>
                <td className="py-1 text-text-muted">
                  {entry ? TIMELINESS.find((t) => t.key === entry.timeliness)?.label : 'not logged'}
                </td>
                <td className="py-1 text-right">
                  {row.score === null ? '—' : formatRating(row.score)}
                </td>
                <td className="py-1 text-right" style={{ color: ratingColour(row.rating) }}>
                  {formatRating(row.rating)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > shown.length && (
        <button
          onClick={() => setAll(true)}
          className="self-start text-xs text-text-muted hover:text-text"
        >
          All {rows.length} days
        </button>
      )}
    </section>
  );
}

/**
 * What matters most, in order. The rank is the weight: the top item is worth
 * as many shares as there are items, the bottom one share. Shown in points for
 * a prayer with everything applying, so moving one visibly moves the stakes.
 */
export function SalahPriority() {
  const queryClient = useQueryClient();
  const settings = useSalahOrder();
  const [order, setOrder] = useState<SalahMetric[] | null>(null);
  useEffect(() => {
    if (settings.data) setOrder(settings.data.order);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (next: SalahMetric[]) => saveSalahSettings(next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['salah-settings'] });
      await queryClient.invalidateQueries({ queryKey: ['salah'] });
      await queryClient.invalidateQueries({ queryKey: ['planet-buildings'] });
      await queryClient.invalidateQueries({ queryKey: ['universe'] });
    },
  });

  const [dragging, setDragging] = useState<number | null>(null);

  if (!order) return null;
  const points = salahPoints('dhuhr', order);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(to, 0, item);
    setOrder(next);
    save.mutate(next);
  };

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs uppercase tracking-wide text-text-muted">What matters most</h3>
      <p className="text-xs text-text-muted">
        Drag to reorder. Higher costs more when missed. Timeliness is separate: late halves the
        prayer, made up later is a fifth. A day left unlogged changes nothing.
      </p>
      <ol className="flex flex-col">
        {order.map((key, i) => {
          const metric = SALAH_METRICS.find((m) => m.key === key);
          if (!metric) return null;
          return (
            <li
              key={key}
              draggable
              onDragStart={() => setDragging(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragging !== null) move(dragging, i);
                setDragging(null);
              }}
              onDragEnd={() => setDragging(null)}
              className="flex cursor-grab items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1 hover:bg-surface-1"
              style={{ opacity: dragging === i ? 0.4 : 1 }}
            >
              <span className="w-5 text-xs tabular-nums text-text-muted">{i + 1}</span>
              <span className="flex-1 text-sm">{metric.label}</span>
              <span className="text-[0.6875rem] tabular-nums text-text-muted">
                {(points.get(key) ?? 0).toFixed(2)}
              </span>
              <button
                onClick={() => move(i, i - 1)}
                disabled={i === 0}
                className="px-1 text-xs text-text-muted hover:text-text disabled:opacity-20"
                aria-label={`Move ${metric.label} up`}
              >
                ▲
              </button>
              <button
                onClick={() => move(i, i + 1)}
                disabled={i === order.length - 1}
                className="px-1 text-xs text-text-muted hover:text-text disabled:opacity-20"
                aria-label={`Move ${metric.label} down`}
              >
                ▼
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
